import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  inject,
  runInInjectionContext,
  Injector,
  DestroyRef,
  Signal,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ErrorStateService,
  AuctionItem,
  AuctionQuery,
  SignalRHub,
  TermsAndCondtionsType,
  getLoyaltyBalance,
  AuctionStatus,
  LOYALTY_STATUS,
  FgbError,
  LoyaltyCategoryType,
} from '@fgb/core';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { NgbCarouselConfig, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuctionPurchaseConfirmationComponent } from './auction-purchase-confirmation/auction-purchase-confirmation.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ScreenType } from '@fgb/portal-component-library/src/lib/directives';
import { Router } from '@angular/router';

@Component({
  selector: 'fgbcl-auction-details',
  templateUrl: './auction-details.component.html',
  styleUrls: ['./auction-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuctionDetailsComponent implements OnInit, OnDestroy {
  @Input() auctionId: number;
  @Input() completeRedirect: string = '/rewards/auctions';
  auction$: Observable<AuctionItem | undefined>;
  availableBalance: number = getLoyaltyBalance();
  bidAmount: number = 0;
  minBidAmount: number = 0;
  auctionEnd: boolean = false;
  isBidOutOfRange: boolean = false;
  isWinning: Signal<boolean> = signal(false);
  currentValue: number;
  showTermsAndConditionsText = false;
  showDescription = true;
  TermsAndCondtionsType = TermsAndCondtionsType;
  termsAccepted: boolean = false;
  screenSize = ScreenType;
  auction: Signal<any> = signal(null);
  STATUS = LOYALTY_STATUS;
  loyaltyCategoryType = LoyaltyCategoryType.Auction;
  public auctionStatus = AuctionStatus;
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  constructor(
    private auctionQuery: AuctionQuery,
    public errorService: ErrorStateService,
    private carouselConfig: NgbCarouselConfig,
    private signalRHub: SignalRHub,
    private router: Router,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.signalRHub
      .joinGroup('AuctionDetails_' + this.auctionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.init();
    this.updateCarouselConfig();
  }

  ngOnDestroy() {
    this.signalRHub.removeFromGroup('AuctionDetails_' + this.auctionId);
  }

  init(): void {
    runInInjectionContext(this.injector, () => {
      this.auction = toSignal(this.auctionQuery.selectAuction(this.auctionId).pipe(tap((auction) => this.updateValues(auction))));
      this.isWinning = computed(() => !!this.auction().IsWinning);
    });
  }

  auctionTimeEnd(event: boolean) {
    this.auctionEnd = event;
    this.router.navigate([this.completeRedirect]);
  }

  updateValues(o?: AuctionItem): void {
    const currentValue = o?.CurrentValue ?? 0;
    const bidIncrement = o?.BidIncrement ?? 0;
    this.minBidAmount = o?.HasPreviousSuccessfulBid ? currentValue + bidIncrement : currentValue;
    this.bidAmount = this.minBidAmount;
    this.availableBalance += o?.UserLastBid || 0;
    this.currentValue = o?.CurrentValue || 0;
  }

  openConfirmation(): void {
    let amountToDebit = this.bidAmount;
    if (this.isWinning()) {
      const auction = this.auctionQuery.getEntity(this.auctionId);
      if (auction) {
        amountToDebit -= auction.UserLastBid;
      }
    }

    const modal = this.modalService.open(AuctionPurchaseConfirmationComponent, {
      ariaLabelledBy: 'confirmation-title',
      centered: true,
      windowClass: 'reward-confirm-modal',
    });
    modal.componentInstance.auctionItem = this.auction();
    modal.componentInstance.bidAmount = this.bidAmount;
    modal.componentInstance.amountToDebit = amountToDebit;
  }

  /**
   * Customise carousel
   * config if needed for
   * auction items which have
   * multiple auction images.
   */
  updateCarouselConfig() {
    this.carouselConfig.showNavigationIndicators = false;
    this.carouselConfig.interval = 3000;
  }

  showDescriptionActive() {
    this.showDescription = true;
    this.showTermsAndConditionsText = false;
  }

  showTermsAndConditonsActive() {
    this.showDescription = false;
    this.showTermsAndConditionsText = true;
  }

  onErrorChange(err: any) {
    // The error message displays for 3 seconds and then clears itself, if not the behavior wanted, use service instead
    if (err) {
      throw new FgbError({ message: err });
    }
  }
}
